import { CompanyDoc, PersonDoc, PersonRoleGroups, ProjectDoc, RoleGroup, VectorStoreAskAudioResult } from 'common';
import { authFetch } from '../../global/service/AuthService';
import { setCompanyDoc, setProjectDoc } from '../../global/store/project-store';

export interface RoleGroupResponse {
  project: ProjectDoc;
  roleGroup?: RoleGroup;
  updatedPeople?: number;
}

const PROJECT_DOC_CACHE_PREFIX = 'project-doc-';
const COMPANY_DOC_CACHE_PREFIX = 'company-doc-';

export class ProjectService {
  private static getBaseUrl(): string {
    return `${globalThis.ccLambdaBaseUrl}/fn/projects`;
  }

  static cacheKey(projectId: string): string {
    return `${PROJECT_DOC_CACHE_PREFIX}${projectId}`;
  }

  static readCachedProjectDoc(projectId: string): ProjectDoc | null {
    try {
      const raw = localStorage.getItem(this.cacheKey(projectId));
      return raw ? (JSON.parse(raw) as ProjectDoc) : null;
    } catch {
      return null;
    }
  }

  static writeCachedProjectDoc(doc: ProjectDoc): void {
    if (!doc?._id) return;
    localStorage.setItem(this.cacheKey(doc._id), JSON.stringify(doc));
    setProjectDoc(doc._id, doc);
  }

  static clearProjectDocCache(): void {
    this.removeLocalStorageKeysWithPrefix(PROJECT_DOC_CACHE_PREFIX);
  }

  static companyCacheKey(companyId: string): string {
    return `${COMPANY_DOC_CACHE_PREFIX}${companyId}`;
  }

  static readCachedCompanyDoc(companyId: string): CompanyDoc | null {
    if (!companyId) return null;
    try {
      const raw = localStorage.getItem(this.companyCacheKey(companyId));
      return raw ? (JSON.parse(raw) as CompanyDoc) : null;
    } catch {
      return null;
    }
  }

  static writeCachedCompanyDoc(doc: CompanyDoc): void {
    if (!doc?._id) return;
    try {
      localStorage.setItem(this.companyCacheKey(doc._id), JSON.stringify(doc));
    } catch (error) {
      console.error(`Failed to cache company ${doc._id}`, error);
    }
  }

  static clearCompanyDocCache(): void {
    this.removeLocalStorageKeysWithPrefix(COMPANY_DOC_CACHE_PREFIX);
  }

  private static removeLocalStorageKeysWithPrefix(prefix: string): void {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keys.push(key);
      }
    }
    keys.forEach(key => localStorage.removeItem(key));
  }

  private static async fetchProjectDoc(projectId: string): Promise<ProjectDoc> {
    const response = await authFetch(`${this.getBaseUrl()}/${encodeURIComponent(projectId)}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to load project ${projectId}`);
    }
    const doc = (await response.json()) as ProjectDoc;
    this.writeCachedProjectDoc(doc);
    return doc;
  }

  /**
   * Get project document by ID. Returns a cached copy immediately when present
   * and refreshes from the API in the background.
   */
  static async getProjectDoc(projectId: string): Promise<ProjectDoc> {
    const cached = this.readCachedProjectDoc(projectId);
    const fetchPromise = this.fetchProjectDoc(projectId);
    if (cached) {
      setProjectDoc(projectId, cached);
      void fetchPromise.catch(error => console.error(`Failed to refresh project ${projectId}`, error));
      return cached;
    }
    return await fetchPromise;
  }

  /**
   * Company for the given project. Returns a cached copy immediately when present
   * (keyed by company_id) and refreshes from the API in the background.
   * Falls back to cache if the request fails (offline, etc.).
   */
  static async getCompanyDoc(projectId: string, companyId: string): Promise<CompanyDoc | null> {
    const fetchPromise = this.fetchCompanyDoc(projectId);

    try {
      return await fetchPromise;
    } catch (error) {
      console.error(`Failed to load company ${companyId}`, error);
      return this.readCachedCompanyDoc(companyId);
    }
  }

  static async fetchCompanyDoc(projectId: string): Promise<CompanyDoc | null> {
    const response = await authFetch(`${this.getBaseUrl()}/${encodeURIComponent(projectId)}/company`);
    if (response.status === 404) return null;
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).message || `Failed to load company for project ${projectId}`);
    }
    const doc = (await response.json()) as CompanyDoc | null;
    if (doc?._id) {
      this.writeCachedCompanyDoc(doc);
    }
    return doc;
  }

  /**
   * Save project document via API and cache the successful response.
   */
  static async saveProjectDoc(doc: ProjectDoc): Promise<ProjectDoc> {
    const response = await authFetch(`${this.getBaseUrl()}/${encodeURIComponent(doc._id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(doc),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to save project ${doc._id}`);
    }
    const saved = (await response.json()) as ProjectDoc;
    this.writeCachedProjectDoc(saved);
    return saved;
  }

  /**
   * Fetch person data for a project document
   */
  static async fetchPerson(projectId: string, documentId: string): Promise<PersonDoc> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/projects/fetch-person';
    const response = await authFetch(url + '?' + new URLSearchParams({ projectId, documentId }));
    return response.json();
  }

  /**
   * Fetches suggested people for a project.
   * Will find employees of company if the project is on a company.
   */
  static async getSuggestedPeople(projectId: string): Promise<PersonDoc[]> {
    const response = await authFetch(`${this.getBaseUrl()}/suggested-people/${encodeURIComponent(projectId)}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).message || 'Failed to load suggested people');
    }
    return (await response.json()) as PersonDoc[];
  }

  /**
   * Bulk-invite employees to a project with role group assignments.
   */
  static async bulkInvite(projectId: string, peopleAndRoleGroups: PersonRoleGroups[]): Promise<PersonDoc[]> {
    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}/fn/auth/invite-bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, peopleAndRoleGroups }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).message || 'Failed to send invitations');
    }
    return (await response.json()) as PersonDoc[];
  }

  /**
   * Create a new role group on the project.
   */
  static async createRoleGroup(projectId: string, roleGroup: Omit<RoleGroup, 'id'>): Promise<RoleGroupResponse> {
    const response = await authFetch(`${this.getBaseUrl()}/${encodeURIComponent(projectId)}/role-groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(roleGroup),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).message || 'Failed to create role group');
    }
    const result = (await response.json()) as RoleGroupResponse;
    setProjectDoc(projectId, result.project);
    return result;
  }

  /**
   * Update an existing role group on the project.
   */
  static async updateRoleGroup(projectId: string, groupId: string, roleGroup: Partial<Omit<RoleGroup, 'id'>>): Promise<RoleGroupResponse> {
    const response = await authFetch(`${this.getBaseUrl()}/${encodeURIComponent(projectId)}/role-groups/${encodeURIComponent(groupId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(roleGroup),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).message || 'Failed to update role group');
    }
    const result = (await response.json()) as RoleGroupResponse;
    setProjectDoc(projectId, result.project);
    return result;
  }

  /**
   * Delete a role group from the project.
   */
  static async deleteRoleGroup(projectId: string, groupId: string): Promise<RoleGroupResponse> {
    const response = await authFetch(`${this.getBaseUrl()}/${encodeURIComponent(projectId)}/role-groups/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).message || 'Failed to delete role group');
    }
    const result = (await response.json()) as RoleGroupResponse;
    setProjectDoc(projectId, result.project);
    return result;
  }

  static async askAudioVectorStore(projectId: string, audio: Blob, previousResponseId?: string): Promise<VectorStoreAskAudioResult> {
    const url = `${globalThis.ccLambdaBaseUrl}/fn/${projectId}/vector-store/ask-audio`;
    const form = new FormData();
    const filename = audio.type?.includes('mp4') || audio.type?.includes('m4a') ? 'audio.m4a' : 'audio.webm';
    form.append('audio', audio, filename);
    if (previousResponseId) {
      form.append('previousResponseId', previousResponseId);
    }
    const response = await authFetch(url, { method: 'POST', body: form });
    const json = await response.json();
    if (!response.ok || json.error) {
      throw new Error(json.error || 'Audio search failed');
    }
    return json as VectorStoreAskAudioResult;
  }
}
